/**
 * MantenimientoSlideOver — Detalle + acciones (Iniciar / Completar / Eliminar).
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Stack, Alert, CircularProgress, Chip,
  TextField, Divider,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import SlideOver from '../common/SlideOver';
import ConfirmDialog from '../common/ConfirmDialog';
import useToast from '../common/useToast';
import {
  API_BASE, getAuthHeaders, fmtFecha, fmtMoney, ESTADOS, CATEGORIAS, TIPOS_EVENTO,
} from './shared/format';

export default function MantenimientoSlideOver({ mantenimientoId, onClose, onUpdated }) {
  const { toast, ToastContainer } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completarOpen, setCompletarOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [completarForm, setCompletarForm] = useState({
    fecha_realizada: '', proveedor: '', costo: '', comprobante_url: '', observaciones: '',
    crear_siguiente: true,
  });

  const cargar = async () => {
    setLoading(true);
    try {
      // No hay GET single — usamos el listar y filtramos. Es MVP.
      const r = await fetch(
        `${API_BASE}/turismo/mantenimiento/mantenimientos?empresa_id=${JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'}`,
        { headers: getAuthHeaders() });
      const list = await r.json();
      const found = list.find(m => m.id === mantenimientoId);
      setData(found);
    } catch (e) { toast.error('Error cargando detalle'); }
    setLoading(false);
  };

  useEffect(() => { cargar(); }, [mantenimientoId]); // eslint-disable-line

  const iniciar = async () => {
    const r = await fetch(`${API_BASE}/turismo/mantenimiento/mantenimientos/${mantenimientoId}/iniciar`, {
      method: 'POST', headers: getAuthHeaders(),
    });
    if (!r.ok) { const e = await r.json(); toast.error(e.detail || 'Error'); return; }
    toast.success('Mantenimiento iniciado');
    cargar(); onUpdated?.();
  };

  const completar = async () => {
    const body = {
      fecha_realizada: completarForm.fecha_realizada || null,
      proveedor: completarForm.proveedor || null,
      costo: completarForm.costo ? Number(completarForm.costo) : null,
      comprobante_url: completarForm.comprobante_url || null,
      observaciones: completarForm.observaciones || null,
      crear_siguiente: completarForm.crear_siguiente,
    };
    const r = await fetch(`${API_BASE}/turismo/mantenimiento/mantenimientos/${mantenimientoId}/completar`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
    });
    if (!r.ok) { const e = await r.json(); toast.error(e.detail || 'Error'); return; }
    const resp = await r.json();
    toast.success(resp.siguiente_mantenimiento_id ? 'Completado. Próximo programado automáticamente.' : 'Completado.');
    setCompletarOpen(false);
    cargar(); onUpdated?.();
    setTimeout(onClose, 800);
  };

  const eliminar = async () => {
    await fetch(`${API_BASE}/turismo/mantenimiento/mantenimientos/${mantenimientoId}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    setConfirmDel(false);
    toast.success('Eliminado');
    onUpdated?.();
    setTimeout(onClose, 400);
  };

  const m = data;
  const cat = m ? (CATEGORIAS.find(c => c.value === m.categoria) || CATEGORIAS[CATEGORIAS.length - 1]) : null;
  const estadoCfg = m ? (ESTADOS[m.estado] || ESTADOS.programado) : null;
  const tipo = m ? (TIPOS_EVENTO.find(t => t.value === m.tipo_evento)?.label || m.tipo_evento) : '';

  return (
    <>
      <SlideOver open onClose={onClose} variant="wide"
        title={m ? m.nombre : 'Cargando…'}
        subtitle={m ? `${m.embarcacion_nombre} · ${tipo}` : ''}
        icon={<BuildIcon />}
        footer={
          <>
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cerrar</Button>
            {m && m.estado === 'programado' && (
              <Button variant="outlined" startIcon={<PlayCircleIcon />} onClick={iniciar}
                sx={{ textTransform: 'none' }}>
                Iniciar
              </Button>
            )}
            {m && (m.estado === 'programado' || m.estado === 'en_curso' || m.estado === 'vencido') && (
              <Button variant="contained" startIcon={<CheckCircleIcon />} disableElevation
                onClick={() => setCompletarOpen(true)}
                sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}>
                Completar
              </Button>
            )}
          </>
        }>
        {loading || !m ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
        ) : (
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={estadoCfg.label}
                sx={{ bgcolor: estadoCfg.bg, color: estadoCfg.color, fontWeight: 700 }} />
              <Chip label={cat.label}
                sx={{ bgcolor: `${cat.color}20`, color: cat.color, fontWeight: 700 }} />
              {m.bloquea_operacion && (
                <Chip label="Bloquea operación"
                  sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700 }} />
              )}
            </Box>

            {m.estado === 'vencido' && (
              <Alert severity="error" sx={{ fontSize: 12 }}>
                Esta tarea está vencida. Programada para {fmtFecha(m.fecha_programada)}.
              </Alert>
            )}

            <Section title="Programación">
              <Row label="Fecha programada" value={fmtFecha(m.fecha_programada)} />
              {m.fecha_inicio && <Row label="Iniciado" value={fmtFecha(m.fecha_inicio)} />}
              {m.fecha_realizada && <Row label="Realizado" value={fmtFecha(m.fecha_realizada)} />}
              {m.proxima_fecha && <Row label="Próximo" value={fmtFecha(m.proxima_fecha)} />}
            </Section>

            {(m.proveedor || m.costo != null) && (
              <Section title="Ejecución">
                {m.proveedor && <Row label="Proveedor" value={m.proveedor} />}
                {m.costo != null && <Row label="Costo" value={fmtMoney(m.costo)} />}
              </Section>
            )}

            {m.observaciones && (
              <Section title="Observaciones">
                <Typography sx={{ fontSize: 12, color: '#475569' }}>{m.observaciones}</Typography>
              </Section>
            )}

            <Divider sx={{ my: 1 }} />
            <Button startIcon={<DeleteIcon />} color="error" onClick={() => setConfirmDel(true)}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
              Eliminar mantenimiento
            </Button>
          </Stack>
        )}
      </SlideOver>

      {/* Diálogo completar */}
      {completarOpen && (
        <SlideOver open onClose={() => setCompletarOpen(false)}
          title="Completar mantenimiento" icon={<CheckCircleIcon />}
          footer={
            <>
              <Button onClick={() => setCompletarOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
              <Button variant="contained" disableElevation onClick={completar}
                sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}>
                Marcar completado
              </Button>
            </>
          }>
          <Stack spacing={1.5}>
            <TextField size="small" type="date" label="Fecha realizada (default hoy)"
              value={completarForm.fecha_realizada} InputLabelProps={{ shrink: true }}
              onChange={e => setCompletarForm({ ...completarForm, fecha_realizada: e.target.value })} />
            <TextField size="small" label="Proveedor"
              value={completarForm.proveedor}
              onChange={e => setCompletarForm({ ...completarForm, proveedor: e.target.value })} />
            <TextField size="small" type="number" label="Costo"
              value={completarForm.costo}
              onChange={e => setCompletarForm({ ...completarForm, costo: e.target.value })} />
            <TextField size="small" label="URL del comprobante"
              value={completarForm.comprobante_url}
              onChange={e => setCompletarForm({ ...completarForm, comprobante_url: e.target.value })}
              placeholder="Link al PDF o foto" />
            <TextField size="small" multiline minRows={3} label="Observaciones"
              value={completarForm.observaciones}
              onChange={e => setCompletarForm({ ...completarForm, observaciones: e.target.value })}
              placeholder="Notas, hallazgos, recomendaciones…" />
            {m?.plantilla_id && (
              <Alert severity="info" sx={{ fontSize: 12 }}>
                Esta tarea proviene de una plantilla. Al completar se programará automáticamente el próximo.
              </Alert>
            )}
          </Stack>
        </SlideOver>
      )}

      <ConfirmDialog open={confirmDel} onClose={() => setConfirmDel(false)} onConfirm={eliminar}
        title="¿Eliminar mantenimiento?"
        message="Esta acción no se puede deshacer."
        confirmText="Eliminar" severity="danger" />

      <ToastContainer />
    </>
  );
}

function Section({ title, children }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0d9488', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
        {title}
      </Typography>
      <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
        {children}
      </Box>
    </Box>
  );
}

function Row({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
      <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a', textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}
